import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Hammer, ArrowRight, Package, AlertTriangle, Camera } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import { fmtDate } from "../lib/format.js";
import CommentThread from "../components/CommentThread.jsx";
import { BpsTransitionDialog } from "../components/bps/BpsTransitionDialog.jsx";
import { BpsAttachmentSlot } from "../components/bps/BpsAttachmentSlot.jsx";
import { ApprovalsPanel, DamagesPanel, ScheduleCard, InvoiceCard } from "../components/bps/BpsPanels.jsx";
import { WORK_TYPE_COLORS, WORK_TYPE_UNDEFINED } from "../lib/bps.js";

const DIALOG_STATES = ["QUOTED", "SCHEDULE", "BILLED"];

const ADMIN_STYLE = { NOT_YET_QUOTED: "bg-yellow-50 text-yellow-800 border-yellow-300", QUOTED: "bg-blue-50 text-blue-800 border-blue-300", "RE-QUOTE": "bg-orange-50 text-orange-800 border-orange-300", AUTHORIZED: "bg-emerald-50 text-emerald-800 border-emerald-300", ORDERED: "bg-indigo-50 text-indigo-800 border-indigo-300", RECEIVED: "bg-teal-50 text-teal-800 border-teal-300", REJECTED: "bg-red-50 text-red-800 border-red-300", CHANGE_RESOLUTION: "bg-purple-50 text-purple-800 border-purple-300" };
const WS_STYLE = { PENDING: "bg-gray-100 text-gray-700 border-gray-300", SCHEDULE: "bg-cyan-50 text-cyan-800 border-cyan-300", "BPS 0 - RECEIVE": "bg-blue-50 text-blue-800 border-blue-300", "BPS 1 - BATECHAPAS": "bg-indigo-50 text-indigo-800 border-indigo-300", "BPS 2 - PREPARAÇÃO": "bg-violet-50 text-violet-800 border-violet-300", "BPS 3 - PINTURA": "bg-pink-50 text-pink-800 border-pink-300", "BPS 4 - ACABAMENTO": "bg-teal-50 text-teal-800 border-teal-300", "EM LAVAGEM": "bg-sky-50 text-sky-800 border-sky-300", RESOLVED: "bg-emerald-50 text-emerald-800 border-emerald-300" };
const FN_STYLE = { OPEN: "bg-yellow-50 text-yellow-800 border-yellow-300", BILLED: "bg-blue-50 text-blue-800 border-blue-300", DELIVERED: "bg-emerald-50 text-emerald-800 border-emerald-300", CLOSED: "bg-gray-100 text-gray-700 border-gray-300", REOPENED: "bg-cyan-50 text-cyan-800 border-cyan-300" };

export default function BodyShopCaseDetailPage() {
  const { id } = useParams();
  const [bps, setBps] = useState(null);
  const [enums, setEnums] = useState(null);
  const [err, setErr] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [dialog, setDialog] = useState(null); // {track, newState}
  const [dialogErr, setDialogErr] = useState("");
  const [busy, setBusy] = useState(false);

  const loadAttachments = async () => {
    const { data } = await api.get(`/attachments?record_type=body_shop_case&record_id=${id}`);
    setAttachments(data);
  };
  const load = async () => {
    const [{ data: b }, { data: e }] = await Promise.all([api.get(`/body-shop-cases/${id}`), api.get(`/body-shop-cases/meta/enums`)]);
    setBps(b); setEnums(e);
  };
  useEffect(() => { load(); loadAttachments(); }, [id]);
  if (!bps || !enums) return <div className="p-8 text-sm text-[#6C757D]">A carregar…</div>;

  const postTransition = async (track, newState, extra = {}) => {
    const { data } = await api.post(`/body-shop-cases/${id}/${track}-state`, { new_state: newState, ...extra });
    setBps(data);
  };

  const doTransition = async (track, newState) => {
    setErr("");
    if (DIALOG_STATES.includes(newState)) { setDialogErr(""); setDialog({ track, newState }); return; }
    let comment = null;
    if (["REJECTED", "CLOSED"].includes(newState)) {
      comment = prompt(`Motivo para ${newState}:`);
      if (!comment) return;
    }
    try { await postTransition(track, newState, { comment }); }
    catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };

  const confirmDialog = async (extra) => {
    setBusy(true); setDialogErr("");
    try { await postTransition(dialog.track, dialog.newState, extra); setDialog(null); }
    catch (e) { setDialogErr(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changeWorkType = async (v) => {
    setErr("");
    try { const { data } = await api.patch(`/body-shop-cases/${id}`, { work_type: v }); setBps(data); }
    catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };

  const adminAllowed = enums.admin_transitions[bps.admin_state] || [];
  const wsAllowed = enums.workshop_transitions[bps.workshop_state] || [];
  const fnAllowed = enums.final_transitions[bps.final_state] || [];
  const lbl = (s) => (enums.short_labels && enums.short_labels[s]) || s;

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 shadow-sm" data-testid="bps-header">
        <div className="flex items-center gap-2 text-xs text-[#6C757D] mb-1">
          <Link to="/body-shop-cases" className="flex items-center gap-1 hover:text-[#1F3864]"><ChevronLeft className="w-3 h-3" /> Bate-chapas</Link>
          <span>/</span><span className="font-mono">{bps.number}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <Hammer className="w-6 h-6 text-[#1F3864]" />
              <span className="font-mono text-sm text-[#6C757D]">{bps.number}</span>
              <span className={`inline-flex text-[11px] font-semibold uppercase tracking-wider border rounded-sm px-2 py-0.5 ${WORK_TYPE_COLORS[bps.work_type] || WORK_TYPE_UNDEFINED}`} data-testid="bps-work-type-badge">{bps.work_type_label}</span>
            </div>
            <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-bold text-[#1A1A2E] leading-tight" data-testid="bps-title">
              <span className="font-mono text-[#1F3864]">{bps.vehicle_registration}</span> · {bps.brand} {bps.model}
            </h1>
            <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 text-base">
              <span data-testid="bps-header-customer">Cliente: <b className="text-[#1A1A2E]">{bps.customer_name}</b> <span className="text-sm text-[#6C757D]">{bps.customer_phone}</span></span>
              <span data-testid="bps-header-insurance">Seguradora: <b className="text-[#1A1A2E]">{bps.insurance_company || "—"}</b></span>
              <span className="text-sm text-[#6C757D] self-center">Dealer: {bps.branch}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-4">
        {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid="bps-error">{err}</div>}

        {/* 3 tracks side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <TrackCard title="Pipeline Administrativa" state={bps.admin_state} style={ADMIN_STYLE} allowed={adminAllowed} onGo={(s) => doTransition("admin", s)} lbl={lbl} testid="admin" />
          <TrackCard title="Pipeline Oficina" state={bps.workshop_state} style={WS_STYLE} allowed={wsAllowed} blocked={(s) => s === "SCHEDULE" && !["AUTHORIZED", "ORDERED", "RECEIVED"].includes(bps.admin_state) ? "Só após Autorizado na pipeline administrativa" : null} onGo={(s) => doTransition("workshop", s)} lbl={lbl} testid="workshop" />
          <TrackCard title="Pipeline Final" state={bps.final_state} style={FN_STYLE} allowed={fnAllowed} blocked={(s) => s === "BILLED" && bps.workshop_state !== "RESOLVED" ? "Só após a oficina estar Resolvido" : null} onGo={(s) => doTransition("final", s)} lbl={lbl} testid="final" />
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-5 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Row label="Matrícula"><span className="font-mono text-[#1F3864] font-semibold">{bps.vehicle_registration}</span></Row>
          <Row label="VIN"><span className="font-mono">{bps.vin || "—"}</span></Row>
          <Row label="Marca / Modelo">{bps.brand} {bps.model}</Row>
          <Row label="Tipo de obra">
            <select value={bps.work_type || ""} onChange={(e) => changeWorkType(e.target.value)} className="border border-gray-300 rounded-sm px-2 py-1 text-sm" data-testid="bps-work-type-select">
              {!bps.work_type && <option value="">Não definido</option>}
              {(enums.work_types || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Row>
          <Row label="Quilometragem"><span className="font-mono" data-testid="bps-mileage">{bps.mileage != null ? `${Number(bps.mileage).toLocaleString("pt-PT")} km` : "—"}</span></Row>
          <Row label="Cliente">{bps.customer_name}</Row>
          <Row label="Telefone">{bps.customer_phone}</Row>
          <Row label="Email">{bps.customer_email || "—"}</Row>
          <Row label="Seguradora">{bps.insurance_company || "—"}</Row>
          <Row label="Nº Peritagem"><span className="font-mono">{bps.peritagem_reference || "—"}</span></Row>
          <Row label="Data do sinistro">{bps.accident_date ? fmtDate(bps.accident_date) : "—"}</Row>
          <Row label="Requer lavagem">{bps.requires_wash ? "Sim" : "Não"}</Row>
          <Row label="Prioridade">{bps.priority}</Row>
          <Row label="Criado">{fmtDate(bps.created_at)}</Row>
          {bps.vr_record_id && <Row label="Ligado a VR"><Link to={`/vehicle-registrations/${bps.vr_record_id}`} className="text-[#1F3864] hover:underline">Ver registo da viatura →</Link></Row>}
          <div className="col-span-2"><Row label="Descrição do sinistro">{bps.incident_description}</Row></div>
          <div className="col-span-2"><Row label="Notas">{bps.notes || "—"}</Row></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ScheduleCard key={`${bps.scheduled_entry_at}-${bps.expected_exit_at}`} bps={bps} onReload={load} setErr={setErr} />
          <InvoiceCard bps={bps} enums={enums} />
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="bps-photos">
          <div className="font-heading font-semibold text-sm flex items-center gap-2 mb-2"><Camera className="w-4 h-4 text-[#1F3864]" /> Fotografias</div>
          <BpsAttachmentSlot bpsId={id} category="photo" label="Fotografias da viatura" multiple accept="image/*" attachments={attachments} onUploaded={loadAttachments} />
        </div>

        {/* Rejection notice */}
        {bps.insurance_rejected && (
          <div className="bg-[#F8D7DA] border border-[#DC3545]/40 text-[#721C24] rounded-md p-4 flex items-start gap-3" data-testid="bps-rejection-notice">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-semibold text-sm">Rejeitado pela seguradora {bps.insurance_company || "—"}</div>
              {bps.rejection_reason && <div className="text-xs mt-1">Motivo: {bps.rejection_reason}</div>}
              {bps.rejected_at && <div className="text-xs mt-1 opacity-75">Em {fmtDate(bps.rejected_at)}</div>}
            </div>
          </div>
        )}

        {/* Linked PEC (internal parts request auto-created on admin_state=ORDERED) */}
        <div className="bg-white border border-gray-200 rounded-md p-4" data-testid="bps-linked-pec">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-[#1F3864]" />
              <div className="font-heading font-semibold text-sm text-[#1A1A2E]">Peças (PEC ligado)</div>
            </div>
            {!bps.linked_pec && (
              <button
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/body-shop-cases/${id}/create-pec`);
                    if (data.linked_pec_id) await load();
                  } catch (e) { setErr(formatError(e.response?.data?.detail)); }
                }}
                data-testid="bps-create-pec-btn"
                className="text-xs border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white rounded-sm px-3 py-1.5"
              >
                Criar PEC interno
              </button>
            )}
          </div>
          {bps.linked_pec ? (
            <div className="mt-3 text-sm flex items-center gap-3">
              <Link to={`/parts-requests/${bps.linked_pec.id}`} className="font-mono text-[#1F3864] font-semibold hover:underline" data-testid="bps-linked-pec-number">
                {bps.linked_pec.number}
              </Link>
              <span className="text-xs text-[#6C757D]">Estado: <b className="text-[#1A1A2E]">{bps.linked_pec.status}</b></span>
              <Link to={`/parts-requests/${bps.linked_pec.id}`} className="text-xs text-[#1F3864] hover:underline ml-auto">Abrir PEC →</Link>
            </div>
          ) : (
            <div className="mt-2 text-xs text-[#6C757D] italic">
              Nenhum PEC ligado. Será criado automaticamente quando o estado administrativo transitar para <b>ORDERED</b>.
            </div>
          )}
        </div>

        {/* BPS v2 — Estimate + QC + Allocation panel */}
        <BpsV2Panel bps={bps} onReload={load} setErr={setErr} attachments={attachments} onAttachmentsChange={loadAttachments} />

        <CommentThread recordType="body_shop_case" recordId={id} />
      </div>
      {dialog && (
        <BpsTransitionDialog bps={bps} newState={dialog.newState} enums={enums} attachments={attachments} onAttachmentsChange={loadAttachments}
          onConfirm={confirmDialog} onClose={() => setDialog(null)} busy={busy} error={dialogErr} />
      )}
    </div>
  );
}

function TrackCard({ title, state, style, allowed, onGo, lbl, testid, blocked = () => null }) {
  return (
    <div className="bg-white border border-gray-200 rounded-md p-4" data-testid={`bps-track-${testid}`}>
      <div className="text-xs font-bold uppercase tracking-wider text-[#1A1A2E] mb-2">{title}</div>
      <div className="mb-3">
        <span className={`inline-flex text-xs font-semibold uppercase tracking-wider border rounded-sm px-2 py-1 ${style[state] || "bg-gray-100 border-gray-300"}`} data-testid={`bps-${testid}-state`}>{lbl(state)}</span>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D] mb-1">Próximas transições</div>
      <div className="flex flex-wrap gap-1.5">
        {allowed.length === 0 ? <span className="text-xs text-[#6C757D] italic">Sem transições</span>
          : allowed.map(s => (
            <button key={s} onClick={() => onGo(s)} disabled={Boolean(blocked(s))} title={blocked(s) || ""} data-testid={`bps-${testid}-btn-${s.replace(/[\s/-]+/g, "-")}`} className="text-[11px] border border-[#1F3864] text-[#1F3864] hover:bg-[#1F3864] hover:text-white rounded-sm px-2 py-1 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#1F3864]">
              <ArrowRight className="w-3 h-3" /> {lbl(s)}
            </button>
          ))}
      </div>
    </div>
  );
}

function Row({ label, children }) { return <div><div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div><div className="text-sm text-[#1A1A2E]">{children}</div></div>; }

// ============ BPS v2 panel (iter 71) ============
function BpsV2Panel({ bps, onReload, setErr, attachments, onAttachmentsChange }) {
  const [tab, setTab] = useState("approvals");
  const pending = (bps.additional_damages || []).filter((d) => d.status === "pending_approval").length;
  return (
    <div className="bg-white border border-gray-200 rounded-md" data-testid="bps-v2-panel">
      <div className="px-4 py-2 border-b border-gray-200 flex gap-1 flex-wrap">
        {[
          { k: "approvals", l: "Aprovações" },
          { k: "allocation", l: "Atribuições" },
          { k: "qc", l: "QC Checklist" },
          { k: "damages", l: pending ? `Danos adicionais (${pending} pendente${pending > 1 ? "s" : ""})` : "Danos adicionais" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`bps-v2-tab-${t.k}`}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px ${tab === t.k ? "border-[#1F3864] text-[#1F3864]" : "border-transparent text-[#6C757D] hover:text-[#1A1A2E]"}`}>
            {t.l}
          </button>
        ))}
      </div>
      <div className="p-4">
        {tab === "approvals" && <ApprovalsPanel bps={bps} attachments={attachments} onAttachmentsChange={onAttachmentsChange} onReload={onReload} setErr={setErr} />}
        {tab === "allocation" && <AllocationPanel bps={bps} onReload={onReload} setErr={setErr} />}
        {tab === "qc" && <QcPanel bps={bps} onReload={onReload} setErr={setErr} />}
        {tab === "damages" && <DamagesPanel bps={bps} attachments={attachments} onAttachmentsChange={onAttachmentsChange} onReload={onReload} setErr={setErr} />}
      </div>
    </div>
  );
}

function AllocationPanel({ bps, onReload, setErr }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    assigned_technician_id: bps.assigned_technician_id || "",
    assigned_painter_id: bps.assigned_painter_id || "",
    assigned_prep_id: bps.assigned_prep_id || "",
  });
  useEffect(() => { api.get("/users/directory").then((r) => setUsers(r.data)); }, []);
  const save = async () => {
    try { await api.post(`/body-shop-cases/${bps.id}/allocate`, form); onReload(); }
    catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3">
      {[
        ["assigned_technician_id", "Técnico de chapa (BPS 1)"],
        ["assigned_prep_id", "Preparador (BPS 2)"],
        ["assigned_painter_id", "Pintor (BPS 3)"],
      ].map(([k, l]) => (
        <label key={k} className="block"><span className="text-[10px] uppercase tracking-widest text-[#6C757D]">{l}</span>
          <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid={`bps-alloc-${k}`}>
            <option value="">—</option>
            {users.filter((u) => !u.deactivated).map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </label>
      ))}
      <button onClick={save} className="w-full bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm py-2 text-sm font-semibold" data-testid="bps-alloc-save">Guardar atribuições</button>
    </div>
  );
}

function QcPanel({ bps, onReload, setErr }) {
  const [qc, setQc] = useState(null);
  useEffect(() => { api.get(`/body-shop-cases/${bps.id}/qc-checklist`).then((r) => setQc(r.data)); }, [bps.id]);
  if (!qc) return <div className="text-sm text-[#6C757D]">A carregar…</div>;
  const toggleItem = (idx, passed) => {
    const items = [...qc.items];
    items[idx] = { ...items[idx], passed };
    setQc({ ...qc, items });
  };
  const submit = async () => {
    try {
      const overall = qc.items.every((it) => it.passed === true);
      await api.post(`/body-shop-cases/${bps.id}/qc-checklist`, { items: qc.items, overall_passed: overall });
      onReload();
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2">
      {qc.completed_at && (
        <div className={`px-3 py-2 border rounded-sm text-sm ${qc.overall_passed ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-[#F8D7DA] border-[#DC3545]/30 text-[#721C24]"}`}>
          {qc.overall_passed ? "✓ QC aprovado em " : "✗ QC falhou em "}{fmtDate(qc.completed_at)}
        </div>
      )}
      {qc.items.map((it, idx) => (
        <div key={it.key || idx} className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2" data-testid={`bps-qc-item-${it.key || idx}`}>
          <div className="text-sm">{it.label}</div>
          <div className="flex gap-1">
            <button onClick={() => toggleItem(idx, true)} className={`text-xs px-2 py-1 rounded-sm border ${it.passed === true ? "bg-emerald-600 text-white border-emerald-600" : "border-gray-300"}`}>✓</button>
            <button onClick={() => toggleItem(idx, false)} className={`text-xs px-2 py-1 rounded-sm border ${it.passed === false ? "bg-[#DC3545] text-white border-[#DC3545]" : "border-gray-300"}`}>✗</button>
          </div>
        </div>
      ))}
      <button onClick={submit} className="w-full bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm py-2 text-sm font-semibold" data-testid="bps-qc-save">Guardar QC</button>
    </div>
  );
}
